import React, { Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { useLocation } from 'react-router-dom'
import block from 'bem-cn'
import { AnimatePresence, motion } from 'framer-motion'

import { projectStyledName } from 'shared/constants'
import { useClearNotAuthData } from 'shared/hooks/useClearNotAuthData'
import { languagesForHTML } from 'shared/locale'
import { mediaSize } from 'shared/style/var'

import { withFetchSlotsLayoutContent } from 'modules/SlotsRoot/components/withFetchSlotsLayoutContent'
import { useDigitainSlotToken } from 'modules/SlotsRoot/hooks/useDigitainSlotToken'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { changeOpenedLeftSidePanel, changeOpenedRightSidePanel } from 'features/sidePanel/actions'
import { selectLanguage } from 'features/userSettings/selectors'

import { ModalContext, ModalQueryContext } from 'components/HOC/withModalQuery/ModalContext'
import withModalQuery from 'components/HOC/withModalQuery/withModalQuery'
import ModalManager from 'components/ModalManager/ModalManager'
import Notify from 'components/Notify/Notify'

import AgentLinksPolling from 'components/AgentLinksPolling'
import BalancePolling from 'components/BalancePolling/BalancePolling'
import CarouselSliderPolling from 'components/CarouselSliderPolling'
import DefineDeviceComponent from 'components/DefineDeviceComponent'
import { DeviceDetect } from 'components/DeviceDetect/DeviceDetect'
import ExternalGamesPolling from 'components/ExternalGamesPolling/ExternalGamesPolling'
import { PromotionsPolling } from 'components/ExternalGamesPolling/PromotionsPolling'
import { Footer } from 'components/Footer/Footer'
import { Header } from 'components/Header/Header'
import LimitsPolling from 'components/LimitsPolling/LimitsPolling'
import NavMenuMobile from 'components/NavMenuMobile/NavMenuMobile'
import BalanceFromBonusNotify from 'components/notifies/BalanceFromBonus/BalanceFromBonusNotify'
import PersonalJackpotNotify from 'components/notifies/PersonalJackpotNotify/PersonalJackpotNotify'
import Spinner from 'components/Spinner'
import { BonusPromotionNotification } from 'components/BonusPromotionNotification/BonusPromotionNotification'

import './App.scss'

const b = block('app')

interface IAppProps {
  modalFilters: ModalQueryContext
}

const App: React.FC<IAppProps> = ({ children, modalFilters }) => {
  const dispatch = useDispatch()

  const location = useLocation()

  useClearNotAuthData()
  useDigitainSlotToken()

  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const lang = useSelector(selectLanguage, shallowEqual)

  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.tablet })

  useEffect(() => {
    /*     dispatch(userSettingsActions.changeLang(lang)) */
    // dispatch(settingsActions.loadSettings())
    // loadUserData()
  }, [])

  useEffect(() => {
    if (!isMobileOrTablet) {
      dispatch(changeOpenedLeftSidePanel(false))
      dispatch(changeOpenedRightSidePanel(false))
    }
  }, [isMobileOrTablet, dispatch])

  useEffect(() => {
    const appContainer = document.getElementById('app-container')
    appContainer?.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }, [location.pathname])

  return (
    <div className={b()}>
      <PersonalJackpotNotify />
      <BalanceFromBonusNotify />
      <BonusPromotionNotification />
      <div className={b('container')} id="app-container">
        <ModalContext.Provider value={modalFilters}>
          <Helmet htmlAttributes={{ lang: languagesForHTML[lang] }} />
          <Notify />
          <LimitsPolling />
          <CarouselSliderPolling />
          {isAuth && <AgentLinksPolling />}
          {isAuth && <PromotionsPolling />}
          <ExternalGamesPolling />

          <ModalManager />
          <DeviceDetect />
          <Header />
          <motion.div initial={{ x: 0 }} transition={{ duration: 0.5 }} className={b('wrapper')}>
            <div className={b('main')}>
              <Helmet>
                <title>{projectStyledName}</title>
                <meta name="description" content={projectStyledName} />
              </Helmet>
              <div className={b('main-inner')}>
                <Suspense fallback={<Spinner isLoading />}>
                  <main className={b('main-content')}>
                    <AnimatePresence exitBeforeEnter>{children}</AnimatePresence>
                  </main>
                </Suspense>
                <Footer />
                {/* {isAuth && !isMobileOrTablet && <AgentLinkButton />} */}
                <DefineDeviceComponent mobileComponent={<NavMenuMobile />} desktopComponent={<></>} />
              </div>
            </div>
          </motion.div>
          <BalancePolling key={`${isAuth}`} />
        </ModalContext.Provider>
      </div>
    </div>
  )
}

export default withModalQuery(withFetchSlotsLayoutContent(App))
